import { createContext, useContext, useState, ReactNode } from "react";
import { EPermission } from "./types";

interface PermissionContextType {
  permissions: string[];
  setPermissions: (permissions: string[]) => void;
  hasPermission: (permission: EPermission) => boolean;
  hasAnyPermission: (permissions: EPermission[]) => boolean;
  hasAllPermissions: (permissions: EPermission[]) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider = ({ children }: PermissionProviderProps) => {
  const [permissions, setPermissions] = useState<string[]>([]);

  const hasPermission = (permission: EPermission): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList: EPermission[]): boolean => {
    return permissionList.some((permission) => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList: EPermission[]): boolean => {
    return permissionList.every((permission) => permissions.includes(permission));
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        setPermissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};
